import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { showToast } from 'stores/ToastStore';
import Spinner from 'components/ui/Spinner';
import Pagination from 'components/v2/PaginationV2';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import Table from 'components/v2/Table';
import AddButton from 'components/v2/buttons/AddButton';
import Checkbox from 'components/v2/forms/Checkbox';
import CheckIcon from 'components/v2/icons/CheckIcon';
import AccessorialDetails from 'components/modules/user/shipper/AccessorialDetails';
import { getAccessorialRequestRule, removeAccessorialRequestRule, } from 'utils/UserServiceAPIUtils';
import SessionStore from 'stores/SessionStore';
import AccessorialAddEditModal from 'components/modules/user/shipper/AccessorialAddEditModal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
const ITEMS_PER_PAGE_LIMIT = 20;
const DETAIL_MODES = {
    ADD: 'add',
    EDIT: 'edit',
    VIEW: 'view',
};
const TABLE_ACTIONS = {
    REMOVE: 'remove',
};
export default function AccessorialsList({ id, className }) {
    const { t } = useTranslation();
    function renderSpinner() {
        return React.createElement(Spinner, { showMessage: true, message: t('LOADING') });
    }
    const [selectedAccessorial, setSelectedAccessorial] = useState();
    const [detailMode, setDetailMode] = useState();
    const [refresh, setRefresh] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [activePage, setActivePage] = useState(1);
    const [selectedAccessorials, setSelectedAccessorials] = useState([]);
    const [userAccessorialData, setUserAccessorialData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [tableAction, setTableAction] = useState('');
    const [accessorialsToRemove, setAccessorialsToRemove] = useState([]);
    const [confirmModalDetails, setConfirmModalDetails] = useState({});
    const canEditSettings = SessionStore.hasShipperOnboardingRole();
    const toggleAllRef = useRef();
    async function fetchUserAccessorials(id) {
        var _a, _b, _c, _d, _e, _f, _g;
        try {
            const userResponse = await getAccessorialRequestRule(id, {
                count: true,
                sort: 'ASC',
                page: activePage,
                per_page: ITEMS_PER_PAGE_LIMIT,
            });
            if (!userResponse) {
                throw new Error('No response from API');
            }
            const accessorialRules = (_c = (_b = (_a = userResponse === null || userResponse === void 0 ? void 0 : userResponse.data) === null || _a === void 0 ? void 0 : _a.getAccessorialRequestRule) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : [];
            const userResponseTotalCount = (_e = (_d = userResponse === null || userResponse === void 0 ? void 0 : userResponse.data) === null || _d === void 0 ? void 0 : _d.getAccessorialRequestRule) === null || _e === void 0 ? void 0 : _e.totalCount;
            const userResponseTotalPages = (_g = (_f = userResponse === null || userResponse === void 0 ? void 0 : userResponse.data) === null || _f === void 0 ? void 0 : _f.getAccessorialRequestRule) === null || _g === void 0 ? void 0 : _g.totalPages;
            setUserAccessorialData(accessorialRules === null || accessorialRules === void 0 ? void 0 : accessorialRules.map((accessorialRule) => {
                return Object.assign({ id: accessorialRule.accessorial }, accessorialRule);
            }));
            setTotalCount(userResponseTotalCount !== null && userResponseTotalCount !== void 0 ? userResponseTotalCount : accessorialRules.length);
            setTotalPages(userResponseTotalPages !== null && userResponseTotalPages !== void 0 ? userResponseTotalPages : 1);
        }
        catch (err) {
            showToast(t('ERROR_FETCHING_DATA'), null, 'error');
            console.error('Error during fetchUserAccessorials: ', err.message);
        }
        finally {
            setIsLoading(false);
        }
    }
    useEffect(() => {
        if (id) {
            setIsLoading(true);
            fetchUserAccessorials(id);
        }
    }, [activePage, refresh, id]);
    useEffect(() => {
        if (accessorialsToRemove === null || accessorialsToRemove === void 0 ? void 0 : accessorialsToRemove.length) {
            setConfirmModalDetails({
                show: true,
                title: t('REMOVE_ACCESSORIAL'),
                message: t('REMOVE_ACCESSORIAL_MESSAGE'),
                onHide: handleRemoveAccessorialsCancel,
                onConfirm: removeAccessorials,
            });
        }
    }, [accessorialsToRemove]);
    function renderFooter() {
        return (React.createElement("tr", null,
            React.createElement("td", { colSpan: "5" },
                React.createElement(Pagination, { perPage: ITEMS_PER_PAGE_LIMIT, page: activePage, total: totalCount, pages: totalPages, showCount: true, onPageClick: (eventKey) => handlePageChange(eventKey) }))));
    }
    function handleToggleAllCheck(checked) {
        var _a;
        if (checked) {
            setSelectedAccessorials(userAccessorialData.map((row) => row.id));
        }
        else {
            setSelectedAccessorials([]);
        }
        if ((_a = toggleAllRef === null || toggleAllRef === void 0 ? void 0 : toggleAllRef.current) === null || _a === void 0 ? void 0 : _a.indeterminate) {
            toggleAllRef.current.indeterminate = false;
        }
    }
    function isIndeterminate(selected) {
        const selectedCount = selected.length;
        return selectedCount > 0 && (userAccessorialData === null || userAccessorialData === void 0 ? void 0 : userAccessorialData.length) !== selectedCount;
    }
    function handleToggleCheck(id) {
        return function (checked) {
            var _a;
            let updatedSelections;
            if (checked) {
                updatedSelections = [...selectedAccessorials, id];
                setSelectedAccessorials(updatedSelections);
            }
            else {
                updatedSelections = selectedAccessorials.filter((item) => item !== id);
                setSelectedAccessorials(updatedSelections);
            }
            if ((_a = toggleAllRef === null || toggleAllRef === void 0 ? void 0 : toggleAllRef.current) === null || _a === void 0 ? void 0 : _a.indeterminate) {
                toggleAllRef.current.indeterminate = isIndeterminate(updatedSelections);
            }
        };
    }
    function handlePageChange(eventKey) {
        setActivePage(eventKey);
    }
    function handleHideModal() {
        setSelectedAccessorial(null);
        setDetailMode(null);
    }
    function handleViewAccessorial(accessorial) {
        return function (e) {
            e.preventDefault();
            setSelectedAccessorial(accessorial);
            setDetailMode(DETAIL_MODES.VIEW);
        };
    }
    function handleEditAccessorial(accessorial) {
        if (!canEditSettings)
            return;
        setSelectedAccessorial(accessorial);
        setDetailMode(DETAIL_MODES.EDIT);
    }
    function handleAddOrEditSaved(accessorial) {
        if (!canEditSettings)
            return;
        setRefresh(accessorial);
    }
    function handleAdd() {
        setDetailMode(DETAIL_MODES.ADD);
    }
    function isViewMode() {
        return detailMode === DETAIL_MODES.VIEW;
    }
    function isEditMode() {
        return detailMode === DETAIL_MODES.EDIT;
    }
    function isAddMode() {
        return detailMode === DETAIL_MODES.ADD;
    }
    function handleAccessorialTableSelect(e) {
        var _a;
        const action = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value;
        if (action === TABLE_ACTIONS.REMOVE) {
            setTableAction(action);
            handleRemoveBulkAccessorials();
        }
    }
    function resetConfirmModal() {
        setConfirmModalDetails({
            show: false,
            title: '',
            message: '',
            onHide: null,
            onConfirm: null,
            affirmative: null,
        });
    }
    async function removeAccessorials() {
        try {
            if (accessorialsToRemove.length) {
                setConfirmModalDetails((modalDetails) => {
                    return Object.assign(Object.assign({}, modalDetails), { show: false });
                });
                await removeAccessorialRequestRule(id, accessorialsToRemove);
                handleToggleAllCheck(false);
                setTableAction('');
                setRefresh(accessorialsToRemove);
                showToast(t('REMOVE_SSR_ACCESSORIALS_SUCCESS'));
            }
        }
        catch (err) {
            console.error('Error during removeAccessorials: ', err.message);
            showToast(t('REMOVE_SSR_ACCESSORIALS_FAILURE'), null, 'error');
        }
    }
    function handleRemoveSingleAccessorial({ accessorial }) {
        setAccessorialsToRemove([accessorial]);
    }
    function handleRemoveBulkAccessorials() {
        setAccessorialsToRemove(selectedAccessorials);
    }
    function handleRemoveAccessorialsCancel() {
        handleToggleAllCheck(false);
        setTableAction('');
        setAccessorialsToRemove([]);
        resetConfirmModal();
    }
    const columns = [
        {
            field: 'id',
            label: '',
            renderHeader: () => (React.createElement(Checkbox, { id: "id-renderHeader", checked: (selectedAccessorials === null || selectedAccessorials === void 0 ? void 0 : selectedAccessorials.length) === (userAccessorialData === null || userAccessorialData === void 0 ? void 0 : userAccessorialData.length), ref: toggleAllRef, onChange: handleToggleAllCheck, disabled: !canEditSettings })),
            render: (value) => {
                return (React.createElement(Checkbox, { id: "id-render", checked: selectedAccessorials.includes(value), onChange: handleToggleCheck(value), disabled: !canEditSettings }));
            },
        },
        {
            field: 'accessorial',
            label: t('ACCESSORIAL'),
            render: (value, row) => {
                return (React.createElement("a", { href: "#", onClick: handleViewAccessorial(row) }, t(value.toUpperCase())));
            },
        },
        {
            field: 'rules',
            label: t('DOCUMENTS'),
            render: (documents) => {
                return (documents === null || documents === void 0 ? void 0 : documents.length) || 0;
            },
        },
        {
            field: 'customer_approval_required',
            label: t('CUSTOMER_APPROVAL_REQUIRED'),
            render: (value, row, index) => {
                if (index < 5 && value) {
                    return (React.createElement("div", { className: "" },
                        React.createElement(CheckIcon, { className: "text-green-500 h-5 w-5" })));
                }
                return null;
            },
        },
        {
            field: 'action',
            label: '',
            render: (_, row, i) => {
                return (React.createElement("div", { className: "flex justify-end" },
                    React.createElement(Dropdown, { disabled: !canEditSettings, useIcon: true },
                        React.createElement(Dropdown.Button, { onClick: () => handleEditAccessorial(row) }, t('EDIT')),
                        React.createElement(Dropdown.Button, { onClick: () => handleRemoveSingleAccessorial(row) }, titleize(t('REMOVE'))))));
            },
        },
    ];
    return isLoading ? (renderSpinner()) : (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "flex justify-between items-center mb-4" },
            React.createElement("div", { className: "flex space-x-2 items-center" },
                React.createElement("h3", { className: "text-base font-semibold leading-6 text-gray-900" }, t('INVOICEABLE_ACCESSORIAL')),
                React.createElement(AddButton, { disabled: !canEditSettings, size: "small", onClick: handleAdd })),
            React.createElement("select", { value: tableAction, onChange: handleAccessorialTableSelect, className: "form-select" },
                React.createElement("option", { value: "" }, titleize(t('SELECT_ACTION'))),
                React.createElement("option", { value: "remove", disabled: !selectedAccessorials.length || !canEditSettings }, titleize(t('REMOVE'))))),
        React.createElement(Table, { columns: columns, footer: renderFooter, data: userAccessorialData, headers: columns, className: "overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg" }),
        React.createElement(AccessorialDetails, { accessorial: selectedAccessorial, onHide: handleHideModal, viewMode: isViewMode() }),
        React.createElement(AccessorialAddEditModal, { shipperId: id, accessorial: selectedAccessorial, editMode: isEditMode(), addMode: isAddMode(), onHide: handleHideModal, onSave: handleAddOrEditSaved }),
        (confirmModalDetails === null || confirmModalDetails === void 0 ? void 0 : confirmModalDetails.show) && (React.createElement(ConfirmModal, { show: confirmModalDetails === null || confirmModalDetails === void 0 ? void 0 : confirmModalDetails.show, title: confirmModalDetails === null || confirmModalDetails === void 0 ? void 0 : confirmModalDetails.title, message: confirmModalDetails === null || confirmModalDetails === void 0 ? void 0 : confirmModalDetails.message, onHide: confirmModalDetails === null || confirmModalDetails === void 0 ? void 0 : confirmModalDetails.onHide, onConfirm: confirmModalDetails === null || confirmModalDetails === void 0 ? void 0 : confirmModalDetails.onConfirm, affirmative: confirmModalDetails === null || confirmModalDetails === void 0 ? void 0 : confirmModalDetails.affirmative }))));
}
